import { useEffect, useRef, useState } from 'react';
import NewBadge from './NewBadge';
import MediaLoadingSpinner from './MediaLoadingSpinner';

/**
 * Cover — preset group / preset image-cover primitive.
 *
 * Figma source: `cover` component-set `123:2927`, 22 variants (type × num
 * × isNew). All variants share the same shell geometry (strict-audit
 * §8 confirmed no bottom-gradient, no text overlay, no invented styles).
 *
 * Shell geometry (manifest §5.4):
 *  - Size 160×160 FIXED/FIXED (w-40 h-40)
 *  - cornerRadius 24 → rounded-home-24
 *  - clipsContent: true → overflow-hidden
 *  - Two visual children:
 *    (1) image N — RECTANGLE with IMAGE fill, scaleMode=FILL. In code:
 *        <img src={coverUrl} className="w-full h-full object-cover" />.
 *        Any demo-text (LUXURY / Yacht / etc) visible in Figma mocks is
 *        BAKED INTO imageRef itself — the primitive must NOT reproduce
 *        text or bottom gradient (strict-audit confirmed invented).
 *    (2) slider/tag (NewBadge) — gated by `isNew` bool, positioned
 *        top-0 right-0 via Figma constraint {vertical:TOP, horizontal:RIGHT}.
 *
 * For `type=packs` (5 variants): still a single image (not 2×2). Strict-
 * audit fixed the prior invention; backend `previewSlideshowUrls`
 * delivers a pre-cooked PNG where the 2×2 grid is part of the image.
 *
 * Size override: stage-03 uses 179×239 preset cards (not 160×160).
 * Caller can pass `style={{ width, height }}` to override. The primitive
 * keeps aspect-agnostic image sizing (object-cover fills any ratio).
 *
 * Props:
 *  - coverUrl (string, required) — image src (from backend / R2)
 *  - alt?     (string)  — empty by default (decorative)
 *  - isNew?   (bool)    — default false
 *  - type?    (string)  — 'viral' | 'lux' | 'packs' | 'hot' — metadata only
 *  - num?     (number)  — 1..6 — metadata only (helps caller pick sample)
 *  - className? passthrough
 *  - style?   passthrough (use for size override)
 *  - eager?   (bool) — set image src immediately for above-the-fold cards
 */
export default function Cover({
  coverUrl,
  alt = '',
  isNew = false,
  eager = false,
  lazyRootMargin = '360px',
  type,
  num,
  className = '',
  style,
  ...rest
}) {
  // type / num are intentionally read-only metadata; they don't change
  // render in this primitive (Figma variants differ only by imageRef,
  // which is now coming from backend). Keep props in signature for
  // documentation + potential future variant-switching.
  void type;
  void num;
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(() => !coverUrl);
  const [shouldLoad, setShouldLoad] = useState(
    () => !coverUrl || eager || typeof IntersectionObserver === 'undefined',
  );

  useEffect(() => {
    if (!coverUrl) {
      setShouldLoad(true);
      setIsLoaded(true);
      return undefined;
    }

    if (eager || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return undefined;
    }

    setShouldLoad(false);
    setIsLoaded(false);

    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: lazyRootMargin, threshold: 0.01 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [coverUrl, eager, lazyRootMargin]);

  useEffect(() => {
    if (!coverUrl) {
      setIsLoaded(true);
      return undefined;
    }

    if (!shouldLoad) {
      setIsLoaded(false);
      return undefined;
    }

    setIsLoaded(false);

    const revealIfCached = () => {
      const img = imgRef.current;
      if (img?.complete) {
        setIsLoaded(true);
      }
    };

    const frame = requestAnimationFrame(revealIfCached);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') revealIfCached();
    };

    window.addEventListener('pageshow', revealIfCached);
    window.addEventListener('focus', revealIfCached);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pageshow', revealIfCached);
      window.removeEventListener('focus', revealIfCached);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [coverUrl, shouldLoad]);

  const cls = [
    'relative w-40 h-40',
    'rounded-home-24',
    'overflow-hidden',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className={cls} style={style} {...rest}>
      {coverUrl && shouldLoad && (
        <img
          ref={imgRef}
          src={coverUrl}
          alt={alt}
          className={[
            'w-full h-full object-cover pointer-events-none transition-opacity duration-200',
            isLoaded ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
          loading={eager ? 'eager' : 'lazy'}
          fetchpriority={eager ? 'high' : 'auto'}
          decoding="async"
          draggable={false}
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)}
        />
      )}
      {!isLoaded && <MediaLoadingSpinner />}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1]"
        data-native-menu-shield="true"
      />
      {/* coverRadius={24} = rounded-home-24 у Cover-обёртки → TR-угол badge'а
          совпадает со скруглением cover'а, без клип-зазора. */}
      {isNew && <NewBadge className="absolute top-0 right-0" coverRadius={24} />}
    </div>
  );
}
